class SocialProofPopup extends HTMLElement {
  constructor() {
    super();
    this.events = [];
    this.timers = new Set();
    this.controller = new AbortController();
    this.shownIndices = [];
    this.shownCount = 0;
    this.cycleStarted = false;
  }

  connectedCallback() {
    const eventsScript = this.querySelector('script[data-events]');
    const stringsScript = this.querySelector('script[data-strings]');
    if (!eventsScript) return;

    try {
      this.events = JSON.parse(eventsScript.textContent || '[]');
    } catch (_err) {
      this.events = [];
    }

    if (this.events.length === 0) return;

    try {
      this.strings = stringsScript ? JSON.parse(stringsScript.textContent || '{}') : {};
    } catch (_err) {
      this.strings = {};
    }

    this.delayMs = Number(this.dataset.delay || 12) * 1000;
    this.showMs = Number(this.dataset.show || 5) * 1000;
    this.intervalMs = Number(this.dataset.interval || 45) * 1000;
    this.maxPerSession = Number(this.dataset.max || 4);
    this.template = this.dataset.messageTemplate || '{name} from {city} just got the {product}';

    this.card = this.querySelector('.sp-card');
    this.thumb = this.querySelector('.sp-thumb__img');
    this.line = this.querySelector('.sp-text__line');
    this.timeEl = this.querySelector('.sp-text__time');
    this.dismissBtn = this.querySelector('.sp-dismiss');

    if (!this.shouldShowAfterDismiss()) return;

    this.shownCount = this.readSessionInt('sp:shown', 0);
    if (this.shownCount >= this.maxPerSession) return;

    this.dismissBtn?.addEventListener('click', () => this.dismiss(), { signal: this.controller.signal });
    document.addEventListener('visibilitychange', () => this.onVisibility(), { signal: this.controller.signal });

    this.removeAttribute('hidden');
    this.scheduleNext(this.delayMs);
  }

  disconnectedCallback() {
    this.controller.abort();
    this.clearTimers();
  }

  /**
   * Learning-mode contribution point: tune dismiss-memory.
   * Returns true if the popup is allowed to render given prior dismissals.
   * Current rule: respect dismissal for the rest of this session, plus 24h via localStorage.
   * Tweak the constant or swap to sessionStorage-only for less aggressive memory.
   */
  shouldShowAfterDismiss() {
    const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
    if (sessionStorage.getItem('sp:dismissed') === '1') return false;
    const dismissedAt = Number(localStorage.getItem('sp:dismissedAt') || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return false;
    return true;
  }

  scheduleNext(waitMs) {
    if (this.shownCount >= this.maxPerSession) return;
    const id = setTimeout(() => {
      this.timers.delete(id);
      if (document.hidden) {
        this.scheduleNext(this.intervalMs);
        return;
      }
      this.show();
    }, waitMs);
    this.timers.add(id);
  }

  show() {
    const event = this.pickEvent();
    if (!event) return;

    this.render(event);

    requestAnimationFrame(() => {
      this.classList.add('is-visible');
    });

    this.shownCount += 1;
    this.writeSessionInt('sp:shown', this.shownCount);

    const id = setTimeout(() => {
      this.timers.delete(id);
      this.hide();
      this.scheduleNext(this.intervalMs);
    }, this.showMs);
    this.timers.add(id);
  }

  hide() {
    this.classList.remove('is-visible');
  }

  dismiss() {
    this.clearTimers();
    this.hide();
    sessionStorage.setItem('sp:dismissed', '1');
    localStorage.setItem('sp:dismissedAt', String(Date.now()));
    const id = setTimeout(() => {
      this.timers.delete(id);
      this.setAttribute('hidden', '');
    }, 500);
    this.timers.add(id);
  }

  render(event) {
    const minutes = this.randomMinutes(event.minutes_min, event.minutes_max);
    const messageText = this.template
      .replaceAll('{name}', event.name || 'Someone')
      .replaceAll('{city}', event.city || '')
      .replaceAll('{product}', event.product_title || '')
      .replace(/\s+/g, ' ')
      .trim();

    if (this.line) this.line.textContent = messageText;
    if (this.timeEl) {
      const tpl = this.strings.time_ago || '{minutes} min ago';
      this.timeEl.textContent = tpl.replace('__N__', String(minutes)).replace('{minutes}', String(minutes));
    }

    if (this.thumb && event.product_image) {
      this.thumb.src = event.product_image;
      this.thumb.alt = event.product_title || '';
    }

    this.card?.classList.remove('sp-card--peek');
    void this.card?.offsetWidth;
    this.card?.classList.add('sp-card--peek');
  }

  pickEvent() {
    if (this.events.length === 0) return null;
    if (this.events.length === 1) return this.events[0];

    const recentWindow = Math.min(3, this.events.length - 1);
    let attempts = 0;
    let idx;
    do {
      idx = Math.floor(Math.random() * this.events.length);
      attempts += 1;
    } while (this.shownIndices.slice(-recentWindow).includes(idx) && attempts < 10);

    this.shownIndices.push(idx);
    if (this.shownIndices.length > recentWindow) this.shownIndices.shift();
    return this.events[idx];
  }

  randomMinutes(min, max) {
    const lo = Math.max(1, Math.floor(Number(min) || 1));
    const hi = Math.max(lo, Math.floor(Number(max) || lo));
    return lo + Math.floor(Math.random() * (hi - lo + 1));
  }

  onVisibility() {
    if (document.hidden) this.clearTimers();
  }

  clearTimers() {
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
  }

  readSessionInt(key, fallback) {
    const v = Number(sessionStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  }

  writeSessionInt(key, value) {
    sessionStorage.setItem(key, String(value));
  }
}

if (!customElements.get('social-proof-popup')) {
  customElements.define('social-proof-popup', SocialProofPopup);
}
